import { formationAPI } from '../services/api';

/** Weekday ids stored on batches (`Monday`, …) — Sunday-first index for `Date.getDay()`. */
export const WEEKDAY_SUN_FIRST = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Map batch day string or numeric index to 0–6 (Sunday = 0). */
export function weekdayToSunFirstIndex(day) {
  if (typeof day === 'number' && day >= 0 && day <= 6) return day;
  if (typeof day === 'string') {
    const idx = WEEKDAY_SUN_FIRST.indexOf(day);
    if (idx >= 0) return idx;
  }
  return null;
}

/** Resolve classroom by id, name, or code (legacy batches may store name). */
export function resolveClassroom(classrooms, roomRef) {
  if (!roomRef || !Array.isArray(classrooms)) return null;
  return (
    classrooms.find(
      (c) => c.id === roomRef || c.name === roomRef || c.code === roomRef
    ) || null
  );
}

/** Display label for batch room field (id or legacy name). */
export function resolveRoomLabel(classrooms, roomRef, fallback = '') {
  const room = resolveClassroom(classrooms, roomRef);
  return room?.name || roomRef || fallback;
}

/** Active enrollment count per course id (via batch → courseId). */
export function countActiveEnrollmentsByCourse(enrollmentsList, batchesList) {
  const batchToCourse = {};
  for (const b of batchesList || []) {
    if (b?.id && b?.courseId) batchToCourse[b.id] = b.courseId;
  }
  const map = {};
  for (const e of enrollmentsList || []) {
    if (e.status !== 'active' || !e.batchId) continue;
    const courseId = batchToCourse[e.batchId];
    if (courseId) map[courseId] = (map[courseId] || 0) + 1;
  }
  return map;
}

/** Whether a batch row belongs to a course (id, code, or legacy name). */
export function batchBelongsToCourse(batch, course) {
  if (!batch || !course) return false;
  return (
    batch.courseId === course.id ||
    batch.courseId === course.courseCode ||
    (course.title && batch.courseName === course.title)
  );
}

/** Count distinct active students enrolled in a course (via batches). */
export function countEnrolledStudentsForCourse(course, batches, enrollments) {
  if (!course) return 0;
  const batchIds = new Set(
    (batches || []).filter((b) => batchBelongsToCourse(b, course)).map((b) => b.id)
  );
  const seen = new Set();
  let count = 0;
  for (const enrollment of enrollments || []) {
    if (enrollment.status !== 'active' || !batchIds.has(enrollment.batchId)) continue;
    if (seen.has(enrollment.studentId)) continue;
    seen.add(enrollment.studentId);
    count += 1;
  }
  return count;
}

/** Resolve enrolled student records for a course from bundle + catalog. */
export function getEnrolledStudentsForCourse(course, batches, enrollments, studentsCatalog) {
  if (!course) return [];
  const batchIds = new Set(
    (batches || []).filter((b) => batchBelongsToCourse(b, course)).map((b) => b.id)
  );
  const byStudentId = Object.fromEntries(
    (studentsCatalog || []).filter((s) => s?.id).map((s) => [s.id, s])
  );
  const seen = new Set();
  const result = [];

  for (const enrollment of enrollments || []) {
    if (enrollment.status !== 'active' || !batchIds.has(enrollment.batchId)) continue;
    if (seen.has(enrollment.studentId)) continue;
    seen.add(enrollment.studentId);

    const student = byStudentId[enrollment.studentId];
    result.push({
      ...(student || {}),
      id: enrollment.studentId,
      name: student
        ? student.name ||
          `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
          student.studentCode ||
          enrollment.studentId
        : enrollment.studentName || enrollment.studentId,
      enrollmentDate: enrollment.enrollmentDate,
      enrollmentId: enrollment.id,
      batchId: enrollment.batchId,
    });
  }

  return result;
}

/** Parse SPA-style `days` array from API row (`daysJson` or legacy `days`). */
export function parseBatchDays(batch) {
  if (!batch) return [];
  if (Array.isArray(batch.days)) return batch.days;
  if (!batch.daysJson) return [];
  try {
    const parsed = typeof batch.daysJson === 'string' ? JSON.parse(batch.daysJson) : batch.daysJson;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Count active enrollments per batch (string batch id keys). */
export function countActiveEnrollmentsByBatch(enrollmentsList) {
  const map = {};
  for (const e of enrollmentsList || []) {
    if (e.status !== 'active' || !e.batchId) continue;
    map[e.batchId] = (map[e.batchId] || 0) + 1;
  }
  return map;
}

/**
 * Mirrors localStorageService.getBatchWithEnrollment shape for UI
 * (`days`, currentEnrollment, availableSpots, isFull).
 */
export function enrichBatchWithEnrollment(batch, countByBatchId) {
  if (!batch) return null;
  const days = parseBatchDays(batch);
  const maxCap = Number(batch.maxCapacity);
  const maxCapacity = Number.isFinite(maxCap) ? maxCap : 15;
  const currentEnrollment = countByBatchId[batch.id] ?? 0;
  return {
    ...batch,
    days,
    currentEnrollment,
    availableSpots: Math.max(0, maxCapacity - currentEnrollment),
    isFull: currentEnrollment >= maxCapacity,
  };
}

/** One round-trip: batches + enrollments → enriched batches list + enrollments array. */
export async function fetchFormationEnrollmentBundle() {
  const [batchRes, enrollRes] = await Promise.all([
    formationAPI.getBatches({}),
    formationAPI.getEnrollments({}),
  ]);
  const enrollments = Array.isArray(enrollRes?.items) ? enrollRes.items : [];
  const countByBatch = countActiveEnrollmentsByBatch(enrollments);
  const batchesRaw = Array.isArray(batchRes?.items) ? batchRes.items : [];
  const batches = batchesRaw.map((b) => enrichBatchWithEnrollment(b, countByBatch));
  return { batches, enrollments };
}
